import { useWidget } from '../../../providers/widget'
import { SpecsWidgetConfig } from '../config'
import { useFetchSpecs } from '../data'
import { useLocaleDetection } from '../../../hooks/locale'
import { useLocalizedTranslation } from '../../../common/i18n'
import SpecsSkeleton from './SpecsSkeleton'
import WidgetDataError from '../../../components/WidgetDataError'
import { formatSpecCount, specCountValue } from '../format'

export default function SpecsList() {
  const { options } = useWidget<SpecsWidgetConfig>()
  const { data: propertyDetails, isLoading, isError, error } = useFetchSpecs(options.filter)

  const { detectedLocale } = useLocaleDetection(options.language.useAutoDetectedLocale, options.language.locale)
  const { _t } = useLocalizedTranslation('specs', options.translations, detectedLocale, options.language)

  if (isLoading) {
    return <SpecsSkeleton />
  }

  if (isError) {
    return <WidgetDataError error={error} />
  }

  if (!propertyDetails) {
    return null
  }

  // Optional area size column (WP plugin specs parity)
  const showArea = options.showAreaSize === true && !!propertyDetails.area_size
  const areaUnitLabel = (propertyDetails.area_size_unit || 'SQUARE_FEET') === 'SQUARE_METERS' ? 'm²' : 'sq ft'
  const columnCount = showArea ? 4 : 3

  return (
    <div className="specs-widget">
      <div
        className="grid gap-0 border border-border rounded-lg overflow-hidden"
        style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
      >
        {/* Bedrooms Section. Zero bedrooms is a studio, not "0 BEDROOMS" —
            show the Studio label with no number (#998). */}
        <div className="flex flex-col items-center p-6 border-r border-border">
          <div className="flex items-center gap-3 mb-3 icomoon-icon text-accent text2x">
            <i className={`${options.icons.bedrooms}`}></i>
            {specCountValue(propertyDetails.bedroom_count) > 0 && (
              <span className="text2x font-bold">
                {formatSpecCount(propertyDetails.bedroom_count)}
              </span>
            )}
          </div>
          <span className="font-medium uppercase tracking-wide">
            {specCountValue(propertyDetails.bedroom_count) > 0 ? _t('bedrooms') : _t('studio')}
          </span>
        </div>

        {/* Bathrooms Section */}
        <div className="flex flex-col items-center p-6 border-r border-border">
          <div className="flex items-center gap-3 mb-3 icomoon-icon text-accent text2x">
            <i className={`${options.icons.bathrooms}`}></i>
            <span className="text2x font-bold">
              {formatSpecCount(propertyDetails.bathroom_count)}
            </span>
          </div>
          <span className="font-medium uppercase tracking-wide">
            {_t('bathrooms')}
          </span>
        </div>

        {/* Guests Section */}
        <div className={`flex flex-col items-center p-6${showArea ? ' border-r border-border' : ''}`}>
          <div className="flex items-center gap-3 mb-3 icomoon-icon text-accent text2x">
            <i className={`${options.icons.guests}`}></i>
            <span className="text2x font-bold">
              {formatSpecCount(propertyDetails.maximum_guest_count || propertyDetails.base_guest_count)}
            </span>
          </div>
          <span className="font-medium uppercase tracking-wide">
            {_t('guests')}
          </span>
        </div>

        {/* Area Size Section (optional) */}
        {showArea && (
          <div className="flex flex-col items-center p-6">
            <div className="flex items-center gap-3 mb-3 icomoon-icon text-accent text2x">
              <i className={`${options.icons.areaSize || 'icon-area'}`}></i>
              <span className="text2x font-bold">
                {formatSpecCount(propertyDetails.area_size)}
              </span>
            </div>
            <span className="font-medium uppercase tracking-wide">
              {_t('areaSize')} ({areaUnitLabel})
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
